import React from 'react';
import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiService } from '../services/api';
import type { ParsedTaskStatusResponse } from '../lib/schemas/task.schema';

type TaskStatusQueryKey = ['task-status', string | null | undefined];

type TaskStatusQueryOptions = UseQueryOptions<
  ParsedTaskStatusResponse,
  unknown,
  ParsedTaskStatusResponse,
  TaskStatusQueryKey
>;

interface SupportedQueryOptions {
  enabled?: boolean;
  refetchInterval?: TaskStatusQueryOptions['refetchInterval'];
  staleTime?: TaskStatusQueryOptions['staleTime'];
  gcTime?: TaskStatusQueryOptions['gcTime'];
  refetchOnWindowFocus?: TaskStatusQueryOptions['refetchOnWindowFocus'];
  refetchOnReconnect?: TaskStatusQueryOptions['refetchOnReconnect'];
  onSuccess?: (data: ParsedTaskStatusResponse) => void;
}

export function useTaskStatusQuery(
  taskId: string | null | undefined,
  options: SupportedQueryOptions = {},
): UseQueryResult<ParsedTaskStatusResponse, unknown> & {
  normalizedStatus: ParsedTaskStatusResponse['normalizedStatus'] | undefined;
} {
  const {
    enabled = true,
    refetchInterval,
    staleTime,
    gcTime,
    refetchOnWindowFocus,
    refetchOnReconnect,
    onSuccess,
  } = options;

  const queryResult = useQuery({
    queryKey: ['task-status', taskId],
    enabled: Boolean(taskId) && enabled,
    queryFn: async () => {
      if (!taskId) {
        throw new Error('Cannot fetch task status without task id');
      }
      return apiService.getTaskStatus(taskId);
    },
    ...(refetchInterval !== undefined && { refetchInterval }),
    ...(staleTime !== undefined && { staleTime }),
    ...(gcTime !== undefined && { gcTime }),
    ...(refetchOnWindowFocus !== undefined && { refetchOnWindowFocus }),
    ...(refetchOnReconnect !== undefined && { refetchOnReconnect }),
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | undefined)?.status;
      if (status === 404 || status === 401) {
        return false;
      }
      return failureCount < 5;
    },
    retryDelay: (failureCount) =>
      Math.min(3000 * 2 ** (failureCount - 1), 30000),
    networkMode: 'online',
  });

  const lastSuccessDataRef = React.useRef<ParsedTaskStatusResponse | null>(
    null,
  );

  React.useEffect(() => {
    if (!onSuccess) {
      return;
    }

    if (queryResult.status !== 'success' || !queryResult.data) {
      return;
    }

    if (lastSuccessDataRef.current === queryResult.data) {
      return;
    }

    lastSuccessDataRef.current = queryResult.data;
    onSuccess(queryResult.data);
  }, [onSuccess, queryResult.status, queryResult.data]);

  return {
    ...queryResult,
    normalizedStatus: queryResult.data?.normalizedStatus,
  };
}
